import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';

import AutoDebitRegistrationForm from 'shared/modules/auto-debit/components/organisms/AutoDebitRegistrationForm';
import AutoDebitRegistrationInstructions from 'shared/modules/auto-debit/components/organisms/AutoDebitRegistrationInstructions';
import AutoDebitRegistrationTerms from 'shared/modules/auto-debit/components/organisms/AutoDebitRegistrationTerms';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import SuccessFail from 'shared/modules/common/components/organisms/SuccessFail';
import {
  ACTION,
  ITEM,
  SCREEN,
} from 'shared/modules/settlement/constants/mixpanel';
import { createCookie } from 'shared/utils/cookie';
import Mixpanel from 'shared/vendor/Mixpanel';

type Props = {
  open: boolean;
  onClose: () => void;
  onDialogClose?: () => void;
  result?: '0' | '1';
};

const INITIAL_STATE = {
  agreementAccepted: false,
  lastName: '',
  firstName: '',
  dateOfBirth: '',
};

const GATEWAY_URL =
  process.env.WEB_ENV === 'production'
    ? 'https://bc-pay.jp/kfgw/F/wf01pc.html'
    : 'https://uat.bc-pay.jp/kfgw/F/wf01pc.html';

const AutoDebitRegistrationDialog = (props: Props) => {
  // Track the progress which screen the user is on
  const [counter, setCounter] = useState(0);

  // We temporarily store the user's inputs here
  // in case he wants to switch screens, so
  // he does not lose the progress he made so far.
  const [data, setData] = useState(INITIAL_STATE);

  // To submit the Kouza Furikae token to MUFJ Factor Gateway,
  // we need to use an HTML form with Shift-JIS encoding
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onKfrequestSuccess = (kfrequestToken: string) => {
    // This will be used by the Lambda function to redirect to the correct URL after the auto-debit flow is finished
    // The Lambda function will delete this cookie afterwards
    createCookie('webRedirectUrl', window.location.href, 1);

    // To go to the ADRS gateway it requires a normal form POST submission
    hiddenInputRef.current?.setAttribute('value', kfrequestToken);
    formRef.current?.submit();
  };

  // Show the result screen when we get the result of the ADRS process from
  // MUFG Factor gateway
  useEffect(() => {
    if (props.result) {
      setCounter(3);
    }
  }, [props.result]);

  return (
    <>
      <Dialog
        title="口座振替のお申し込み"
        open={props.open}
        showBackButton={!!counter && counter !== 3}
        onBackButtonPress={() => setCounter(counter - 1)}
        onCloseButtonPress={props.onClose}
        onBackdropPress={props.onClose}
        onOpen={() => {
          Mixpanel.timeEvent(
            `${SCREEN.AUTO_DEBIT_REGISTRATION_INSTRUCTION} - ${ACTION.PAGE_VIEW}`
          );
        }}
        onClose={() => {
          Mixpanel.trackEvent(
            `${SCREEN.AUTO_DEBIT_REGISTRATION_INSTRUCTION} - ${ACTION.PAGE_VIEW}`
          );

          // When the dialog is closed, reset the counter.
          // So that when it's opened again, it shows the first screen.
          setCounter(0);
          setData(INITIAL_STATE);
          props.onDialogClose?.();
        }}
        style={style.dialog}
        id="adr-dialog"
      >
        {counter === 0 && (
          <AutoDebitRegistrationInstructions
            onNextButtonPress={() => {
              Mixpanel.trackEvent(
                `${SCREEN.AUTO_DEBIT_REGISTRATION_INSTRUCTION} - ${ACTION.CLICK}`,
                {
                  Item: ITEM.NEXT,
                }
              );

              setCounter(1);
            }}
            onViewListButtonPress={() => {
              Mixpanel.trackEvent(
                `${SCREEN.AUTO_DEBIT_REGISTRATION_INSTRUCTION} - ${ACTION.CLICK}`,
                {
                  Item: ITEM.SUPPORTED_BANKS,
                }
              );

              window.open(
                'https://www.muf.bk.mufg.jp/collect/net_bank.html',
                '_blank'
              );
            }}
          />
        )}
        {counter === 1 && (
          <AutoDebitRegistrationTerms
            data={data}
            onDataUpdate={(newData) => setData({ ...data, ...newData })}
            onNextButtonPress={() => setCounter(2)}
          />
        )}
        {counter === 2 && (
          <AutoDebitRegistrationForm
            data={data}
            onDataUpdate={(newData) => setData({ ...data, ...newData })}
            onSuccess={onKfrequestSuccess}
          />
        )}
        {counter === 3 && (
          <SuccessFail
            message={{
              success: '口座振替お申し込み手続き完了しました',
              fail:
                '銀行口座の登録に失敗しました。お手数ですが、もう一度お試しください。',
            }}
            success={props.result === '1'}
          />
        )}
      </Dialog>
      <form
        ref={formRef}
        acceptCharset="Shift-JIS"
        action={GATEWAY_URL}
        method="POST"
      >
        <input ref={hiddenInputRef} type="hidden" name="kfrequest" />
      </form>
    </>
  );
};

const style = StyleSheet.create({
  dialog: {
    width: 424,
    height: '100%',
    maxHeight: 560,
  },
});

export default AutoDebitRegistrationDialog;
